/**
 * Off-Topic Indicator Component
 * Subtle badge showing that a message was off-topic
 * Non-intrusive design with tooltip explaining why suggestions are shown
 * Following soft, modern styling
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Lightbulb } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { getOffTopicTooltip } from '../utils/suggestionCopy';

interface OffTopicIndicatorProps {
  className?: string;
  size?: 'sm' | 'md';
  showTooltip?: boolean;
}

/**
 * Off-topic indicator badge
 * Small, subtle icon badge positioned near message timestamp
 */
export const OffTopicIndicator: React.FC<OffTopicIndicatorProps> = ({
  className,
  size = 'sm',
  showTooltip = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isTapped, setIsTapped] = useState(false);

  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const containerSize = size === 'sm' ? 'p-1' : 'p-1.5';

  return (
    <div className={cn('relative inline-flex items-center', className)}>
      <motion.button
        type="button"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onTap={() => setIsTapped(!isTapped)}
        className={cn(
          'inline-flex items-center justify-center',
          'rounded-full',
          'bg-blue-50 dark:bg-blue-950/20',
          'border border-blue-200 dark:border-blue-800',
          'text-blue-600 dark:text-blue-400',
          'transition-colors duration-200',
          'hover:bg-blue-100 dark:hover:bg-blue-950/30',
          'focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
          'cursor-help',
          containerSize
        )}
        aria-label={getOffTopicTooltip()}
        title={getOffTopicTooltip()}
      >
        <Lightbulb className={cn(iconSize, 'shrink-0')} />
      </motion.button>

      {/* Tooltip */}
      <AnimatePresence>
        {(isHovered || isTapped) && showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'absolute bottom-full left-1/2 -translate-x-1/2 mb-2',
              'px-3 py-2 rounded-lg',
              'bg-gray-900 dark:bg-gray-800',
              'text-white text-xs font-medium',
              'shadow-lg',
              'whitespace-nowrap',
              'z-50',
              'pointer-events-none',
              'max-w-[200px]'
            )}
            style={{
              filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
            }}
          >
            {getOffTopicTooltip()}
            {/* Arrow */}
            <div
              className={cn(
                'absolute top-full left-1/2 -translate-x-1/2 -mt-1',
                'w-2 h-2 rotate-45',
                'bg-gray-900 dark:bg-gray-800'
              )}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

